/**
 * Step4ExtractedData — hydratation active des valeurs canoniques Q7.a
 * (9.T.5.v3.6, QA §2 + §3).
 *
 * Alignements QA §2 :
 *   - Identifier visuellement les champs qui seront appliqués (indication
 *     "Sera appliqué au project/client")
 *   - Séparer clairement les champs `product.*` / `coverage.*` en lecture-seule via
 *     `ExtractionReviewPanel` en bas (panel autonome, non-bindé)
 *   - Ne JAMAIS inclure automatiquement un field `needs_review=true` — filtrés
 *     à l'hydratation initiale
 *   - Les valeurs `_raw` non-canoniques restent visibles mais non appliquées
 *
 * Alignements QA §3 :
 *   - Priorité aux overrides humains (`override_by` non-null)
 *   - Source consolidée canonique via `useExtractionFields` (v3.3 parent-agnostic)
 */

import type { ExtractedFieldRow } from "@kollectivia/shared";
import { GUARANTEES, type GuaranteeCode } from "@kollectivia/shared/constants";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { ExtractionReviewPanel } from "../../../../components/extraction/ExtractionReviewPanel";
import { GuaranteeMultiSelect } from "../../../../components/GuaranteeMultiSelect";
import { InsurerMultiSelect } from "../../../../components/InsurerMultiSelect";
import { ApiException } from "../../../../lib/api";
import { getApiErrorMessage, normalizeLang } from "../../../../lib/apiErrorMessage";
import { extractionErrorCodeKey } from "../../../../lib/extractionErrorCode";
import {
  useExtractionFields,
  useExtractionsByParent,
  useStartExtraction,
} from "../../../../lib/queries/extractions";
import { useEventDocumentLinks } from "../../../../lib/queries/intakeLinks";
import { toast } from "../../../../lib/toast";

import type { WizardData } from "./MaterializeWizard";

// 9.T.5.v3.6 QA polish B — filtre registry-based pour les guarantee codes.
// Les codes hors registry (bug prompt LLM ou dette V32-D `_raw` suffix) sont
// écartés silencieusement — ils restent visibles dans `ExtractionReviewPanel`
// autonome en bas (section lecture-seule Q7.b).
//
// 9.V.2c-2b — les insurer_codes ne sont PLUS filtrés côté client : un code
// extrait absent du registre actif (assureur désactivé/inconnu) DOIT rester
// visible et retirable (invariant anti-perte R1). `InsurerMultiSelect` gère
// l'affichage lisible + le statut. Le catalogue guarantee reste statique (hors
// scope 9.V.2c-2b).
const GUARANTEE_CODES_SET = new Set<string>(GUARANTEES.map((g) => g.code));

function filterGuaranteeCodes(arr: string[] | undefined): GuaranteeCode[] {
  if (!arr) return [];
  return arr.filter((c): c is GuaranteeCode => GUARANTEE_CODES_SET.has(c));
}

type Props = {
  intakeEventId: string;
  data: WizardData;
  onChange: (patch: Partial<WizardData>) => void;
};

/**
 * Extrait la valeur canonique pour un field_key donné à partir de la liste
 * consolidée. Priorité aux fields avec `override_by NOT NULL` + `needs_review=false`
 * (QA §3 : overrides humains prioritaires).
 */
function pickCanonicalValue(fields: ExtractedFieldRow[], key: string): unknown {
  const candidates = fields.filter((f) => f.field_key === key && !f.needs_review);
  if (candidates.length === 0) return undefined;
  // Override humain gagne si présent
  const humanOverride = candidates.find((f) => f.override_by !== null);
  if (humanOverride) return humanOverride.value;
  // Sinon dernière consolidée (déjà triée par backend v3.3)
  return candidates[0]!.value;
}

function pickNumericValue(fields: ExtractedFieldRow[], key: string): number | undefined {
  const v = pickCanonicalValue(fields, key);
  if (typeof v === "number") return v;
  if (typeof v === "string") {
    const n = Number(v);
    return Number.isFinite(n) ? n : undefined;
  }
  return undefined;
}

function pickStringArray(fields: ExtractedFieldRow[], key: string): string[] | undefined {
  const v = pickCanonicalValue(fields, key);
  if (Array.isArray(v)) return v.filter((x): x is string => typeof x === "string");
  return undefined;
}

function pickIsoDate(fields: ExtractedFieldRow[], key: string): string | undefined {
  const v = pickCanonicalValue(fields, key);
  if (typeof v !== "string") return undefined;
  // Accept YYYY-MM-DD only (Zod DateOnlySchema).
  if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return v;
  return undefined;
}

export function Step4ExtractedData({ intakeEventId, data, onChange }: Props) {
  const { t, i18n } = useTranslation();
  const lang = normalizeLang(i18n.resolvedLanguage);
  // 9.T.5.v3.6 QA post-fix #5 — polling actif tant qu'aucune extraction terminale
  // (done|failed) présente. Après clic « Lancer l'extraction », le mutate crée
  // une row status=pending → refetch 3s → status=done → polling s'arrête auto
  // (isTerminal dans le hook). Aucun refresh manuel broker requis.
  const extractionsQ = useExtractionsByParent("intake_event", intakeEventId, {
    pollIntervalMs: 3000,
  });
  const docLinksQ = useEventDocumentLinks(intakeEventId);
  const startExtraction = useStartExtraction();
  const doneExtraction = useMemo(
    () => extractionsQ.data?.items.find((e) => e.status === "done"),
    [extractionsQ.data],
  );
  const runningExtraction = useMemo(
    () => extractionsQ.data?.items.find((e) => e.status === "pending" || e.status === "running"),
    [extractionsQ.data],
  );
  const failedExtraction = useMemo(
    () =>
      !doneExtraction && !runningExtraction
        ? extractionsQ.data?.items.find((e) => e.status === "failed")
        : undefined,
    [extractionsQ.data, doneExtraction, runningExtraction],
  );
  const fieldsQ = useExtractionFields(doneExtraction?.id);
  const fields = useMemo<ExtractedFieldRow[]>(() => fieldsQ.data?.items ?? [], [fieldsQ.data]);

  // Hydratation initiale one-shot — QA §3 : overrides humains prioritaires
  // + filtre needs_review=true déjà appliqué dans pickCanonicalValue.
  // Ne re-écrase pas les valeurs déjà saisies par le broker (idempotent).
  useEffect(() => {
    if (fields.length === 0) return;
    // Si applyProjectData déjà set (broker a modifié OR draft resumé), ne rien faire.
    if (data.applyProjectData || data.applyCompanyData) return;

    // 9.T.5.v3.6 QA post-fix #6 : le pass v3.2 `intake_extract_details` produit
    // des field_keys namespacés `project.*` / `company.*` (cf. INTAKE_FIELD_KEY_
    // NAMESPACES apps/shared/src/prompts/intake-extraction.ts). Le mapping doit
    // matcher ces clés exactes, pas les versions non-préfixées.
    const insurer_codes = pickStringArray(fields, "project.insurer_codes");
    const guarantee_codes = pickStringArray(fields, "project.guarantee_codes");
    const effect_date = pickIsoDate(fields, "project.effect_date");
    const renewal_target_date = pickIsoDate(fields, "project.renewal_target_date");
    const deadline_date = pickIsoDate(fields, "project.deadline_date");
    const employee_count = pickNumericValue(fields, "company.employee_count");

    const applyProject: NonNullable<WizardData["applyProjectData"]> = {};
    if (insurer_codes) applyProject.insurer_codes = insurer_codes;
    if (guarantee_codes) applyProject.guarantee_codes = guarantee_codes;
    if (effect_date) applyProject.effect_date = effect_date;
    if (renewal_target_date) applyProject.renewal_target_date = renewal_target_date;
    if (deadline_date) applyProject.deadline_date = deadline_date;

    const applyCompany: NonNullable<WizardData["applyCompanyData"]> = {};
    if (employee_count !== undefined) applyCompany.employee_count = employee_count;

    const patch: Partial<WizardData> = {};
    if (Object.keys(applyProject).length > 0) patch.applyProjectData = applyProject;
    if (Object.keys(applyCompany).length > 0) patch.applyCompanyData = applyCompany;
    if (Object.keys(patch).length > 0) onChange(patch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fields.length]);

  if (extractionsQ.isLoading || fieldsQ.isLoading) {
    return (
      <section>
        <h3 className="mb-1 text-lg font-medium text-slate-900">
          {t("intake_events.detail.materialize.wizard.step_4_extracted_data.title")}
        </h3>
        <p className="text-sm text-slate-400" aria-live="polite">
          {t("app.loading")}
        </p>
      </section>
    );
  }

  // 1e-B — des valeurs canoniques peuvent avoir été PRÉREMPLIES (mapping
  // `field.role` depuis la form_response) sans qu'aucune extraction n'existe.
  // Elles doivent alors être VISIBLES et ÉDITABLES pour revue humaine explicite.
  const hasCanonicalPrefill = !!(data.applyProjectData || data.applyCompanyData);

  if (!doneExtraction && !hasCanonicalPrefill) {
    // QA #7 v3.6 — CTA "Lancer l'extraction" quand aucune extraction done.
    // Utilise `useStartExtraction` v3.3 avec `pass_id: intake_extract_details`
    // (backend v3.2). Requires au moins un file attaché à l'intake_event.
    // QA post-fix #5 : si une extraction pending/running existe déjà, on
    // affiche l'état "Extraction en cours…" avec spinner et on masque le
    // bouton pour empêcher les doubles lancements.
    const firstFileId = docLinksQ.data?.items?.[0]?.file_id ?? null;
    const isRunning = !!runningExtraction || startExtraction.isPending;
    const canLaunch = !!firstFileId && !isRunning && !docLinksQ.isLoading;

    const onLaunch = async () => {
      if (!firstFileId) return;
      try {
        await startExtraction.mutateAsync({
          source: { kind: "intake_event", id: intakeEventId },
          file_id: firstFileId,
          pass_id: "intake_extract_details",
        });
        toast.success(
          t("intake_events.detail.materialize.wizard.step_4_extracted_data.launching_extraction"),
        );
      } catch (err) {
        if (err instanceof ApiException) {
          toast.error(getApiErrorMessage(err, lang) || t("errors.generic"));
        } else {
          toast.error(t("errors.generic"));
        }
      }
    };

    return (
      <section>
        <h3 className="mb-1 text-lg font-medium text-slate-900">
          {t("intake_events.detail.materialize.wizard.step_4_extracted_data.title")}
        </h3>
        <p className="mb-4 text-sm text-slate-600">
          {t("intake_events.detail.materialize.wizard.step_4_extracted_data.subtitle")}
        </p>
        {isRunning ? (
          <div className="border-brand-200 bg-brand-50 rounded-md border p-4" aria-live="polite">
            <p className="text-brand-900 flex items-center gap-2 text-sm font-medium">
              <span
                aria-hidden="true"
                className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-current border-t-transparent"
              />
              {t(
                "intake_events.detail.materialize.wizard.step_4_extracted_data.extraction_in_progress",
              )}
            </p>
          </div>
        ) : failedExtraction ? (
          <div className="rounded-md border border-red-200 bg-red-50 p-4">
            <p className="mb-1 text-sm font-medium text-red-900" role="alert">
              {t("intake_events.detail.materialize.wizard.step_4_extracted_data.extraction_failed")}
            </p>
            {/* 9.W.2 — affichait `error_message` BRUT, sans aucun filtre : le
                message du fournisseur d'IA ou un fragment du document client
                pouvait s'afficher ici. Remplacé par le code de type fermé,
                localisé ; aucun repli sur le message. */}
            {failedExtraction.error_code && (
              <p className="mb-3 text-sm text-red-800">
                {t(extractionErrorCodeKey(failedExtraction.error_code))}
              </p>
            )}
            <button
              type="button"
              onClick={onLaunch}
              disabled={!canLaunch}
              className="btn-primary focus-visible:ring-brand-500 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
            >
              {t(
                "intake_events.detail.materialize.wizard.step_4_extracted_data.retry_extraction_cta",
              )}
            </button>
          </div>
        ) : (
          <div className="rounded-md border border-slate-200 bg-slate-50 p-4">
            <p className="mb-1 text-sm font-medium text-slate-800">
              {t("intake_events.detail.materialize.wizard.step_4_extracted_data.no_extraction")}
            </p>
            <p className="mb-3 text-sm text-slate-600">
              {t(
                "intake_events.detail.materialize.wizard.step_4_extracted_data.no_extraction_body",
              )}
            </p>
            <button
              type="button"
              onClick={onLaunch}
              disabled={!canLaunch}
              className="btn-primary focus-visible:ring-brand-500 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
            >
              {startExtraction.isPending
                ? t(
                    "intake_events.detail.materialize.wizard.step_4_extracted_data.launching_extraction",
                  )
                : t(
                    "intake_events.detail.materialize.wizard.step_4_extracted_data.launch_extraction_cta",
                  )}
            </button>
          </div>
        )}
      </section>
    );
  }

  const proj = data.applyProjectData ?? {};
  const comp = data.applyCompanyData ?? {};

  return (
    <section>
      <h3 className="mb-1 text-lg font-medium text-slate-900">
        {t("intake_events.detail.materialize.wizard.step_4_extracted_data.title")}
      </h3>
      <p className="mb-4 text-sm text-slate-600">
        {t("intake_events.detail.materialize.wizard.step_4_extracted_data.subtitle")}
      </p>

      {!doneExtraction && (
        <div
          role="status"
          className="border-brand-200 bg-brand-50 text-brand-900 mb-4 rounded-md border p-3 text-sm"
        >
          {t("intake_events.detail.materialize.wizard.step_4_extracted_data.prefill_review_notice")}
        </div>
      )}

      <div className="space-y-4">
        <div>
          <h4 className="text-brand-700 mb-2 text-sm font-semibold uppercase tracking-wide">
            {t(
              "intake_events.detail.materialize.wizard.step_4_extracted_data.apply_project_section",
            )}
          </h4>
          <div className="space-y-3">
            <div>
              <label
                htmlFor="materialize-step4-insurer-codes"
                className="block text-xs font-medium text-slate-700"
              >
                {t(
                  "intake_events.detail.materialize.wizard.step_4_extracted_data.insurer_codes_label",
                )}
              </label>
              <div className="mt-1">
                <InsurerMultiSelect
                  id="materialize-step4-insurer-codes"
                  value={proj.insurer_codes ?? []}
                  onChange={(next) =>
                    onChange({
                      applyProjectData: {
                        ...proj,
                        insurer_codes: next.length > 0 ? next : undefined,
                      },
                    })
                  }
                />
              </div>
            </div>

            <div>
              <label
                htmlFor="materialize-step4-guarantee-codes"
                className="block text-xs font-medium text-slate-700"
              >
                {t(
                  "intake_events.detail.materialize.wizard.step_4_extracted_data.guarantee_codes_label",
                )}
              </label>
              <div className="mt-1">
                <GuaranteeMultiSelect
                  id="materialize-step4-guarantee-codes"
                  value={filterGuaranteeCodes(proj.guarantee_codes)}
                  onChange={(next) =>
                    onChange({
                      applyProjectData: {
                        ...proj,
                        guarantee_codes: next.length > 0 ? next : undefined,
                      },
                    })
                  }
                />
              </div>
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <DateFieldRow
                id="materialize-step4-effect-date"
                label={t(
                  "intake_events.detail.materialize.wizard.step_4_extracted_data.effect_date_label",
                )}
                value={proj.effect_date ?? ""}
                onChange={(v) =>
                  onChange({ applyProjectData: { ...proj, effect_date: v || undefined } })
                }
              />
              <DateFieldRow
                id="materialize-step4-renewal-date"
                label={t(
                  "intake_events.detail.materialize.wizard.step_4_extracted_data.renewal_target_date_label",
                )}
                value={proj.renewal_target_date ?? ""}
                onChange={(v) =>
                  onChange({
                    applyProjectData: { ...proj, renewal_target_date: v || undefined },
                  })
                }
              />
              <DateFieldRow
                id="materialize-step4-deadline-date"
                label={t(
                  "intake_events.detail.materialize.wizard.step_4_extracted_data.deadline_date_label",
                )}
                value={proj.deadline_date ?? ""}
                onChange={(v) =>
                  onChange({ applyProjectData: { ...proj, deadline_date: v || undefined } })
                }
              />
            </div>
          </div>
        </div>

        <div>
          <h4 className="text-brand-700 mb-2 text-sm font-semibold uppercase tracking-wide">
            {t(
              "intake_events.detail.materialize.wizard.step_4_extracted_data.apply_company_section",
            )}
          </h4>
          <div>
            <label
              htmlFor="materialize-step4-employee-count"
              className="block text-xs font-medium text-slate-700"
            >
              {t(
                "intake_events.detail.materialize.wizard.step_4_extracted_data.employee_count_label",
              )}
            </label>
            <input
              id="materialize-step4-employee-count"
              type="number"
              inputMode="numeric"
              min={0}
              step={1}
              value={comp.employee_count?.toString() ?? ""}
              onChange={(e) => {
                const raw = e.target.value;
                if (raw === "") {
                  onChange({ applyCompanyData: { ...comp, employee_count: undefined } });
                  return;
                }
                const n = Number(raw);
                onChange({
                  applyCompanyData: {
                    ...comp,
                    employee_count: Number.isFinite(n) ? n : undefined,
                  },
                });
              }}
              placeholder="0"
              className="focus-visible:border-brand-500 focus-visible:ring-brand-500 mt-1 w-full max-w-xs rounded border border-slate-300 p-2 text-sm focus-visible:outline-none focus-visible:ring-2"
            />
          </div>
        </div>

        {doneExtraction && (
          <>
            <p className="rounded-md bg-slate-50 p-3 text-xs text-slate-600">
              {t("intake_events.detail.materialize.wizard.step_4_extracted_data.read_only_notice")}
              <br />
              {t(
                "intake_events.detail.materialize.wizard.step_4_extracted_data.read_only_hidden_needs_review",
              )}
            </p>

            {/* Panel autonome lecture-seule Q7.b product.*/}
            <div className="border-t border-slate-200 pt-4">
              <ExtractionReviewPanel parentKind="intake_event" parentId={intakeEventId} />
            </div>
          </>
        )}
      </div>
    </section>
  );
}

function DateFieldRow({
  id,
  label,
  value,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div>
      <label htmlFor={id} className="block text-xs font-medium text-slate-700">
        {label}
      </label>
      <input
        id={id}
        type="date"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="focus-visible:border-brand-500 focus-visible:ring-brand-500 mt-1 w-full rounded border border-slate-300 p-2 text-sm focus-visible:outline-none focus-visible:ring-2"
      />
    </div>
  );
}
